import Vue from "vue";
import Vuex, { ActionContext } from "vuex";

import { Mode, Token } from "@/types";
import { auth } from "@/store/auth";
import { evidences } from "@/store/evidences";
import { matches } from "@/store/matches";
import { details } from "@/store/details";
import { history } from "@/store/history";
import { settings } from "@/store/settings";
import { alerts } from "@/store/alerts";
import { verify } from "@/store/verify";
import { Modes } from "@/modes";
import {iclive} from "@/store/iclive";

Vue.use(Vuex);

export interface RootState {
  status: "idle" | "loading" | "failed";
  mode: Mode;
  tokens: readonly Token[];
  blocks?: readonly number[][];
  time?: number;
  text: string;
  raw: string;
  codes: string;
}

export default new Vuex.Store<RootState>({
  state: {
    status: "idle",
    mode: Modes.Encode,
    tokens: [],
    time: undefined,
    blocks: undefined,
    text: "",
    raw: "",
    codes: ""
  },
  mutations: {
    UPDATE_MODE(state: RootState, value: Mode) {
      state.mode = value;
    },
    UPDATE_TOKENS(state: RootState, value: Token[]) {
      state.tokens = Object.freeze(value);
    },
    UPDATE_BLOCKS(state: RootState, value: number[][]) {
      state.blocks = Object.freeze(value);
    },
    UPDATE_TIME(state: RootState, value: number) {
      state.time = value;
    },
    UPDATE_TEXT(state: RootState, value: string) {
      state.text = value;
    },
    UPDATE_RAW(state: RootState, value: string) {
      state.raw = value;
    },
    UPDATE_CODES(state: RootState, value: string) {
      state.codes = value;
    },
    UPDATE_STATUS(state: RootState, value: "idle" | "loading" | "failed") {
      state.status = value;
    },
    CLEAR(state: RootState, payload: { keepRaw?: boolean }) {
      state.status = 'idle';
      state.tokens = [];
      state.blocks = undefined;
      state.time = undefined;
      state.text = "";
      state.raw = payload.keepRaw ? state.raw : "";
      state.codes = "";
    }
  },
  actions: {
    updateMode(context: ActionContext<RootState, RootState>, value: Mode) {
      context.commit("UPDATE_MODE", value);
    },
    updateRaw(context: ActionContext<RootState, RootState>, value: string) {
      context.commit("UPDATE_RAW", value);
    },
    updateCodes(context: ActionContext<RootState, RootState>, value: string) {
      context.commit("UPDATE_CODES", value);
    },
    clear(
      context: ActionContext<RootState, RootState>,
      payload: { keepRaw?: boolean }
    ) {
      context.commit("details/CLEAR", null, { root: true });
      context.commit("evidences/CLEAR", null, { root: true });
      context.commit("matches/CLEAR", null, { root: true });
      context.commit("history/CLEAR", null, { root: true });
      context.commit("CLEAR", payload || {});
    }
  },
  getters: {
    tokenPositions(state: RootState) {
      return Object.assign(
        {},
        ...state.tokens.map((token: Token, index: number) => ({
          [token.span[0]]: index
        }))
      );
    }
  },
  modules: {
    auth,
    alerts,
    evidences,
    matches,
    details,
    history,
    settings,
    verify,
    iclive
  }
});
